import { Button, Input, Popover, Tooltip, message } from "antd"
import type { ColumnsType } from "antd/es/table"
import { ChevronDownIcon } from "@heroicons/react/24/outline"
import {
  Children,
  isValidElement,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  useTransition,
  type Dispatch,
  type MouseEvent as ReactMouseEvent,
  type SetStateAction,
  type ThHTMLAttributes,
} from "react"
import { ColumnVisibilityGroup } from "./ColumnVisibilityGroup"
import {
  DEFAULT_COLUMN_WIDTH,
  DEFAULT_VIRTUAL_ROW_THRESHOLD,
  MAX_PINNED_ROWS,
  PIN_COLUMN_KEY,
  REPORT_TABLE_DROPDOWN_Z_INDEX,
  SERIAL_COLUMN_KEY,
} from "./constants"
import { FreezeColumnsPanel } from "./FreezeColumnsPanel"
import { buildPinColumn } from "./PinColumn"
import {
  buildColumnSchema,
  filterColumns,
  getColumnKey,
  resolveVirtualScrollX,
  resolveVirtualScrollY,
  toggleColumnGroup,
  toggleLeafColumn,
} from "./reportTableUtils"
import { EnhancedReportTableProps, ReportColumn } from "./types"
import { AppTable } from "components/new-stuff/app-table"
import { useSeedActionFreeze } from "./useSeedActionFreeze"

export function EnhancedReportTable<T extends Record<string, any>>({
  columns = [],
  dataSource = [],
  loading,
  rowKey,
  defaultFreezeBoundaryKey,
  scroll,
  className,
  tableLayout = "auto",
  bordered,
  title,
  locale,
  pagination = false,
  summary,
  exportFooter,
  exportSheetName,
  enableVirtualization = false,
  virtualRowThreshold = DEFAULT_VIRTUAL_ROW_THRESHOLD,
  toolbarLeading,
  toolbarActions,
  onChange,
  freezeGroupedColumnsByLeaf = false,
  serialNumberOffset = 0,
}: EnhancedReportTableProps<T>) {
  const tableWrapperRef = useRef<HTMLDivElement>(null)
  const [hiddenColumnKeys, setHiddenColumnKeys] = useState<string[]>([])
  const [frozenColumnKeys, setFrozenColumnKeys] = useState<string[]>(
    defaultFreezeBoundaryKey ? [defaultFreezeBoundaryKey] : []
  )
  const [pinnedRowKeys, setPinnedRowKeys] = useState<string[]>([])
  const [columnSearch, setColumnSearch] = useState("")
  const [columnWidths, setColumnWidths] = useState<Record<string, number>>({})
  const [viewportHeight, setViewportHeight] = useState(() =>
    typeof window === "undefined" ? 0 : window.innerHeight
  )
  const [, startTransition] = useTransition()

  const sizedColumns = useMemo(
    () => applyColumnWidths(columns, columnWidths),
    [columnWidths, columns]
  )
  const columnSchema = useMemo(
    () => buildColumnSchema(sizedColumns, freezeGroupedColumnsByLeaf),
    [freezeGroupedColumnsByLeaf, sizedColumns]
  )
  useSeedActionFreeze(
    columnSchema.leaves,
    defaultFreezeBoundaryKey,
    setFrozenColumnKeys
  )

  const allLeafKeys = useMemo(
    () => columnSchema.leaves.map((leaf) => leaf.key),
    [columnSchema.leaves]
  )
  const hiddenColumnKeySet = useMemo(
    () => new Set(hiddenColumnKeys),
    [hiddenColumnKeys]
  )
  const visibleLeafKeys = useMemo(
    () => allLeafKeys.filter((key) => !hiddenColumnKeySet.has(key)),
    [allLeafKeys, hiddenColumnKeySet]
  )
  const hiddenCount = hiddenColumnKeys.length
  const normalizedColumnSearch = columnSearch.trim().toLowerCase()
  const searchedColumnGroups = useMemo(() => {
    if (!normalizedColumnSearch) return columnSchema.groups

    return columnSchema.groups
      .map((group) => {
        const matchesGroup =
          group.title.toLowerCase().includes(normalizedColumnSearch) ||
          group.key.toLowerCase().includes(normalizedColumnSearch)
        const matchingLeaves = matchesGroup
          ? group.leaves
          : group.leaves.filter(
              (leaf) =>
                leaf.title.toLowerCase().includes(normalizedColumnSearch) ||
                leaf.key.toLowerCase().includes(normalizedColumnSearch)
            )

        if (!matchingLeaves.length) return null

        return {
          ...group,
          leaves: matchingLeaves,
        }
      })
      .filter(Boolean) as typeof columnSchema.groups
  }, [columnSchema.groups, normalizedColumnSearch])

  const rowKeyResolver = useMemo(() => {
    if (typeof rowKey === "function") {
      return (record: T, index?: number) => String(rowKey(record, index))
    }

    return (record: T) => String(record[rowKey] ?? "")
  }, [rowKey])

  const rowKeyedData = useMemo(() => {
    const keyedRows = new Map<string, T>()
    dataSource.forEach((row, index) => {
      keyedRows.set(rowKeyResolver(row, index), row)
    })
    return keyedRows
  }, [dataSource, rowKeyResolver])
  const rowNumberMap = useMemo(() => {
    const numberMap = new Map<string, number>()
    dataSource.forEach((row, index) => {
      numberMap.set(rowKeyResolver(row, index), serialNumberOffset + index + 1)
    })
    return numberMap
  }, [dataSource, rowKeyResolver, serialNumberOffset])
  const pinnedRowKeySet = useMemo(() => new Set(pinnedRowKeys), [pinnedRowKeys])
  const pinnedRowIndexMap = useMemo(() => {
    const indexMap = new Map<string, number>()
    pinnedRowKeys.forEach((key, index) => indexMap.set(key, index))
    return indexMap
  }, [pinnedRowKeys])

  useEffect(() => {
    if (!pinnedRowKeys.length) return

    setPinnedRowKeys((current) =>
      current.filter((key) => rowKeyedData.has(key))
    )
  }, [pinnedRowKeys.length, rowKeyedData])

  const togglePinnedRow = useCallback(
    (record: T, index?: number) => {
      const key = rowKeyResolver(record, index)
      startTransition(() => {
        setPinnedRowKeys((current) => {
          if (current.includes(key)) {
            return current.filter((item) => item !== key)
          }

          if (current.length >= MAX_PINNED_ROWS) {
            message.warning(`You can pin up to ${MAX_PINNED_ROWS} rows.`)
            return current
          }

          return [...current, key]
        })
      })
    },
    [rowKeyResolver, startTransition]
  )

  const visibleFreezableColumns = useMemo(
    () =>
      columnSchema.freezableColumns.filter((item) =>
        item.leafKeys.some((key) => visibleLeafKeys.includes(key))
      ),
    [columnSchema.freezableColumns, visibleLeafKeys]
  )
  const visibleFreezableColumnKeys = useMemo(
    () => new Set(visibleFreezableColumns.map((item) => item.key)),
    [visibleFreezableColumns]
  )
  useEffect(() => {
    setFrozenColumnKeys((current) => {
      const next = current.filter((key) => visibleFreezableColumnKeys.has(key))
      return next.length === current.length ? current : next
    })
  }, [visibleFreezableColumnKeys])

  const frozenLeafKeys = useMemo(() => {
    const freezableByKey = new Map(
      visibleFreezableColumns.map((item) => [item.key, item])
    )
    return frozenColumnKeys.flatMap(
      (key) =>
        freezableByKey
          .get(key)
          ?.leafKeys.filter((leafKey) => visibleLeafKeys.includes(leafKey)) ||
        []
    )
  }, [frozenColumnKeys, visibleFreezableColumns, visibleLeafKeys])
  const filteredColumns = useMemo(
    () =>
      filterColumns(sizedColumns, {
        hiddenColumnKeySet,
        frozenLeafKeys,
      }),
    [frozenLeafKeys, hiddenColumnKeySet, sizedColumns]
  )
  const pinColumn = useMemo(
    () => buildPinColumn<T>(pinnedRowKeySet, rowKeyResolver, togglePinnedRow),
    [pinnedRowKeySet, rowKeyResolver, togglePinnedRow]
  )
  const serialColumn = useMemo<ReportColumn>(
    () => ({
      title: "S/N",
      key: SERIAL_COLUMN_KEY,
      width: columnWidths[SERIAL_COLUMN_KEY] || 72,
      fixed: "left",
      className: "report-serial-cell",
      render: (_value: unknown, record: T, index: number) =>
        rowNumberMap.get(rowKeyResolver(record, index)) || index + 1,
    }),
    [columnWidths, rowKeyResolver, rowNumberMap]
  )
  const visibleColumns = useMemo(
    () => [pinColumn, serialColumn, ...filteredColumns] as ReportColumn[],
    [filteredColumns, pinColumn, serialColumn]
  )
  const resizableColumns = useMemo(
    () => attachResizeHandlers(visibleColumns, setColumnWidths),
    [visibleColumns]
  )
  const exportColumns = useMemo(
    () => [serialColumn, ...filteredColumns] as ReportColumn[],
    [filteredColumns, serialColumn]
  )
  const exportColumnSchema = useMemo(
    () => buildColumnSchema(exportColumns),
    [exportColumns]
  )
  const exportFooterRows = useMemo(
    () =>
      exportFooter?.map((row) => [
        {
          value: "",
        },
        ...row,
      ]) || [],
    [exportFooter]
  )
  const resolvedExportSheetName = useMemo(
    () => exportSheetName || (title ? getReactNodeText(title()) : undefined),
    [exportSheetName, title]
  )

  useEffect(() => {
    if (typeof window === "undefined") return

    const handleResize = () => setViewportHeight(window.innerHeight)
    window.addEventListener("resize", handleResize)

    return () => window.removeEventListener("resize", handleResize)
  }, [])

  const orderedData = useMemo(() => {
    if (!pinnedRowKeys.length) return dataSource

    const pinnedRows = pinnedRowKeys
      .map((key) => rowKeyedData.get(key))
      .filter(Boolean) as T[]
    const mainRows = dataSource.filter(
      (row, index) => !pinnedRowKeySet.has(rowKeyResolver(row, index))
    )

    return [...pinnedRows, ...mainRows]
  }, [dataSource, pinnedRowKeySet, pinnedRowKeys, rowKeyResolver, rowKeyedData])

  const virtualScrollX = useMemo(
    () => resolveVirtualScrollX(scroll?.x, visibleColumns),
    [scroll?.x, visibleColumns]
  )
  const virtualScrollY = useMemo(
    () => resolveVirtualScrollY(scroll?.y, viewportHeight),
    [scroll?.y, viewportHeight]
  )
  const shouldVirtualize =
    enableVirtualization &&
    pagination === false &&
    !pinnedRowKeys.length &&
    orderedData.length >= virtualRowThreshold
  const virtualBodyHeight = Math.min(
    virtualScrollY,
    Math.max(120, orderedData.length * 54)
  )
  // Pass the caller's scroll config through untouched when not virtualizing so
  // antd creates an inner scroll container. That is what makes the sticky
  // header and `position: sticky` pinned rows actually pin against the table
  // body instead of disappearing with the page scroll.
  const tableScroll = shouldVirtualize
    ? {
        ...scroll,
        x: virtualScrollX,
        y: virtualBodyHeight,
      }
    : scroll

  const freezeOptions = useMemo(
    () => [
      {
        label: "No freeze",
        value: "",
      },
      ...visibleFreezableColumns.map((item) => ({
        label: item.title,
        value: item.key,
      })),
    ],
    [visibleFreezableColumns]
  )
  const frozenColumnKeySet = useMemo(
    () => new Set(frozenColumnKeys),
    [frozenColumnKeys]
  )
  const freezeButtonLabel = frozenColumnKeys.length
    ? `Freeze columns (${frozenColumnKeys.length})`
    : "No freeze"

  useLayoutEffect(() => {
    const wrapper = tableWrapperRef.current
    if (!wrapper || !pinnedRowKeys.length) return

    let animationFrame = 0
    const pinnedRows = Array.from(
      wrapper.querySelectorAll<HTMLTableRowElement>(
        ".enhanced-report-table .report-row-pinned"
      )
    )

    const updatePinnedOffsets = () => {
      let top = 0
      pinnedRows.forEach((row) => {
        row.style.setProperty("--report-pinned-top", `${top}px`)
        top += row.getBoundingClientRect().height
      })
    }

    const scheduleUpdate = () => {
      cancelAnimationFrame(animationFrame)
      animationFrame = requestAnimationFrame(updatePinnedOffsets)
    }

    scheduleUpdate()

    const resizeObserver =
      typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(scheduleUpdate)
        : null
    pinnedRows.forEach((row) => resizeObserver?.observe(row))
    window.addEventListener("resize", scheduleUpdate)

    return () => {
      cancelAnimationFrame(animationFrame)
      resizeObserver?.disconnect()
      window.removeEventListener("resize", scheduleUpdate)
    }
  }, [orderedData, pinnedRowKeys, visibleColumns])

  const columnsPanel = (
    <div className="w-[320px] max-w-[80vw] font-avenir">
      <div className="mb-1 flex items-center justify-between gap-3">
        <span className="text-sm font-semibold text-neutral600">
          Show / hide columns
        </span>
        <button
          type="button"
          className="text-xs font-semibold text-primary500"
          onClick={() => {
            startTransition(() => setHiddenColumnKeys([]))
          }}
        >
          Reset to default
        </button>
      </div>
      <p className="mb-3 text-xs leading-[1.45] text-neutral300">
        Uncheck a column to hide it from the report. Check it again to bring it
        back.
      </p>
      <Input
        allowClear
        className="mb-3"
        placeholder="Search columns"
        value={columnSearch}
        onChange={(event) => setColumnSearch(event.target.value)}
      />
      <div className="flex max-h-[360px] flex-col gap-3 overflow-y-auto pr-1">
        {searchedColumnGroups.map((group) => (
          <ColumnVisibilityGroup
            key={group.key}
            group={group}
            hiddenColumnKeys={hiddenColumnKeys}
            visibleLeafCount={visibleLeafKeys.length}
            onToggleGroup={() =>
              startTransition(() =>
                setHiddenColumnKeys((current) =>
                  toggleColumnGroup(current, group, allLeafKeys)
                )
              )
            }
            onToggleLeaf={(leafKey) =>
              startTransition(() =>
                setHiddenColumnKeys((current) =>
                  toggleLeafColumn(current, leafKey, allLeafKeys)
                )
              )
            }
          />
        ))}
        {!searchedColumnGroups.length && (
          <div className="rounded-md border border-neutral40 px-3 py-4 text-center text-sm text-neutral300">
            No columns found
          </div>
        )}
      </div>
    </div>
  )
  const getPopupContainer = () => document.body

  const freezePanel = (
    <FreezeColumnsPanel
      frozenColumnKeySet={frozenColumnKeySet}
      frozenColumnKeys={frozenColumnKeys}
      freezeOptions={freezeOptions}
      setFrozenColumnKeys={setFrozenColumnKeys}
    />
  )

  return (
    <div
      ref={tableWrapperRef}
      className="report-table-wrapper flex flex-col gap-3"
    >
      <div
        className={`report-table-toolbar flex items-center gap-2 overflow-x-auto font-avenir ${
          toolbarLeading
            ? "flex-nowrap justify-between"
            : "flex-wrap justify-end"
        }`}
      >
        {toolbarLeading ? (
          <div className="min-w-[220px] flex-1">{toolbarLeading}</div>
        ) : null}
        <div className="flex flex-shrink-0 flex-nowrap items-center gap-2">
          {toolbarActions}
          <Popover
            content={freezePanel}
            trigger="click"
            placement="bottomRight"
            autoAdjustOverflow
            getPopupContainer={getPopupContainer}
            overlayClassName="enhanced-report-table-popover"
            zIndex={REPORT_TABLE_DROPDOWN_Z_INDEX}
          >
            <Tooltip
              title="Pin columns to the left so they stay visible while you scroll the report"
              placement="top"
              color="#0f172b"
              overlayClassName="enhanced-report-table-tooltip"
              getPopupContainer={getPopupContainer}
            >
              <Button className="inline-flex min-w-[190px] items-center justify-between gap-2">
                <span>{freezeButtonLabel}</span>
                <ChevronDownIcon className="h-4 w-4" />
              </Button>
            </Tooltip>
          </Popover>
          <Popover
            content={columnsPanel}
            trigger="click"
            placement="bottomRight"
            autoAdjustOverflow
            getPopupContainer={getPopupContainer}
            overlayClassName="enhanced-report-table-popover"
            zIndex={REPORT_TABLE_DROPDOWN_Z_INDEX}
          >
            <Tooltip
              title="Show or hide columns in this report"
              placement="top"
              color="#0f172b"
              overlayClassName="enhanced-report-table-tooltip"
              getPopupContainer={getPopupContainer}
            >
              <Button>
                Show / hide columns
                {hiddenCount ? ` (${hiddenCount} hidden)` : ""}
              </Button>
            </Tooltip>
          </Popover>
        </div>
      </div>

      <AppTable
        loading={loading}
        dataSource={orderedData}
        columns={resizableColumns as ColumnsType<T>}
        pagination={pagination}
        scroll={tableScroll}
        sticky
        virtual={shouldVirtualize}
        tableLayout={tableLayout}
        bordered={bordered}
        title={title}
        locale={locale}
        summary={summary}
        onChange={onChange}
        components={{
          header: {
            cell: ResizableHeaderCell,
          },
        }}
        rowKey={(record, index) => rowKeyResolver(record, index)}
        rowClassName={(record, index) => {
          const pinnedIndex = pinnedRowIndexMap.get(
            rowKeyResolver(record, index)
          )
          return pinnedIndex !== undefined
            ? `report-row-pinned report-row-pinned-${pinnedIndex}`
            : ""
        }}
        className={`enhanced-report-table ${className || ""}`}
      />
      <table
        data-report-export-table="true"
        data-report-sheet-name={resolvedExportSheetName}
        className="report-export-table"
        style={{
          position: "fixed",
          left: 0,
          top: 0,
          width: 0,
          height: 0,
          overflow: "hidden",
          opacity: 0,
          pointerEvents: "none",
          clipPath: "inset(100%)",
        }}
      >
        <thead>
          <tr>
            {exportColumnSchema.groups.map((group) => (
              <th
                key={group.key}
                colSpan={group.leaves.length > 1 ? group.leaves.length : 1}
                rowSpan={group.leaves.length > 1 ? 1 : 2}
              >
                {group.title}
              </th>
            ))}
          </tr>
          <tr>
            {exportColumnSchema.groups.flatMap((group) =>
              group.leaves.length > 1
                ? group.leaves.map((leaf) => (
                    <th key={leaf.key}>{leaf.title}</th>
                  ))
                : []
            )}
          </tr>
        </thead>
        <tbody>
          {orderedData.map((record, rowIndex) => (
            <tr key={rowKeyResolver(record, rowIndex)}>
              {exportColumnSchema.leaves
                .filter((leaf) => leaf.key !== PIN_COLUMN_KEY)
                .map((leaf) => {
                  const exportCell = getExportCellValue(
                    leaf.column,
                    record,
                    rowIndex
                  )
                  return (
                    <td
                      key={leaf.key}
                      className={exportCell.className}
                    >
                      {exportCell.value}
                    </td>
                  )
                })}
            </tr>
          ))}
        </tbody>
        {exportFooterRows.length ? (
          <tfoot>
            {exportFooterRows.map((row, rowIndex) => (
              <tr key={`export-footer-${rowIndex}`}>
                {row.map((cell, cellIndex) => (
                  <td
                    key={`export-footer-${rowIndex}-${cellIndex}`}
                    className={cell.className}
                  >
                    {cell.value}
                  </td>
                ))}
              </tr>
            ))}
          </tfoot>
        ) : null}
      </table>
    </div>
  )
}

const getExportCellValue = <T extends Record<string, any>>(
  column: ReportColumn,
  record: T,
  rowIndex: number
) => {
  const value = getDataIndexValue(record, column.dataIndex)

  if (typeof column.render === "function") {
    return normalizeExportCellValue(column.render(value, record, rowIndex))
  }

  return normalizeExportCellValue(value)
}

const applyColumnWidths = (
  columns: ReportColumn[],
  widths: Record<string, number>,
  parentKey?: string
): ReportColumn[] =>
  columns.map((column, index) => {
    const columnKey = getColumnKey(column, index, parentKey)
    const children = column.children?.length
      ? applyColumnWidths(column.children, widths, columnKey)
      : undefined

    if (children) {
      return {
        ...column,
        children,
        width: children.reduce(
          (total, child) => total + Number(child.width || DEFAULT_COLUMN_WIDTH),
          0
        ),
      }
    }

    return {
      ...column,
      width: widths[columnKey] || Number(column.width || DEFAULT_COLUMN_WIDTH),
    }
  })

const attachResizeHandlers = (
  columns: ReportColumn[],
  setColumnWidths: Dispatch<SetStateAction<Record<string, number>>>,
  parentKey?: string
): ReportColumn[] =>
  columns.map((column, index) => {
    const columnKey = getColumnKey(column, index, parentKey)

    if (column.children?.length) {
      return {
        ...column,
        children: attachResizeHandlers(
          column.children,
          setColumnWidths,
          columnKey
        ),
      }
    }

    if (columnKey === PIN_COLUMN_KEY) return column

    return {
      ...column,
      onHeaderCell: () => ({
        width: Number(column.width || DEFAULT_COLUMN_WIDTH),
        onResizeStart: createResizeStartHandler(
          columnKey,
          Number(column.width || DEFAULT_COLUMN_WIDTH),
          setColumnWidths
        ),
      }),
    }
  })

const createResizeStartHandler =
  (
    columnKey: string,
    initialWidth: number,
    setColumnWidths: Dispatch<SetStateAction<Record<string, number>>>
  ) =>
  (event: ReactMouseEvent<HTMLSpanElement>) => {
    event.preventDefault()
    event.stopPropagation()

    const startX = event.clientX
    const startWidth = initialWidth || DEFAULT_COLUMN_WIDTH

    // Mouse moves can fire 100s/sec; coalesce into one state update per
    // animation frame so the table re-renders at most ~60 times/sec instead
    // of trailing the cursor.
    let pendingWidth = startWidth
    let frame: number | null = null

    const flush = () => {
      frame = null
      setColumnWidths((current) => {
        if (current[columnKey] === pendingWidth) return current
        return { ...current, [columnKey]: pendingWidth }
      })
    }

    const handleMouseMove = (moveEvent: MouseEvent) => {
      pendingWidth = Math.max(72, startWidth + moveEvent.clientX - startX)
      if (frame !== null) return
      frame = requestAnimationFrame(flush)
    }

    const handleMouseUp = () => {
      document.removeEventListener("mousemove", handleMouseMove)
      document.removeEventListener("mouseup", handleMouseUp)
      if (frame !== null) {
        cancelAnimationFrame(frame)
        frame = null
        flush()
      }
      document.documentElement.classList.remove("report-column-resizing")
      document.body.classList.remove("report-column-resizing")
      document.body.style.userSelect = ""
    }

    document.documentElement.classList.add("report-column-resizing")
    document.body.classList.add("report-column-resizing")
    document.body.style.userSelect = "none"
    document.addEventListener("mousemove", handleMouseMove)
    document.addEventListener("mouseup", handleMouseUp)
  }

const ResizableHeaderCell = ({
  onResizeStart,
  className,
  children,
  ...props
}: ThHTMLAttributes<HTMLTableCellElement> & {
  onResizeStart?: (event: ReactMouseEvent<HTMLSpanElement>) => void
}) => (
  <th
    {...props}
    className={`${className || ""} report-resizable-header-cell`}
  >
    {children}
    {onResizeStart ? (
      <span
        role="separator"
        aria-orientation="vertical"
        className="report-column-resize-handle"
        onClick={(event) => event.stopPropagation()}
        onMouseDown={onResizeStart}
      />
    ) : null}
  </th>
)

const normalizeExportCellValue = (
  value: any
): { value: any; className: string } => {
  if (!isValidElement(value)) {
    return { value: value ?? "", className: "" }
  }

  return {
    value: getReactNodeText(value),
    className: getReactNodeClassName(value),
  }
}

const getReactNodeText = (node: any): string => {
  if (node === null || node === undefined || typeof node === "boolean")
    return ""
  if (typeof node === "string" || typeof node === "number") return String(node)
  if (Array.isArray(node)) return node.map(getReactNodeText).join("")

  if (isValidElement(node)) {
    return Children.toArray((node.props as any)?.children)
      .map(getReactNodeText)
      .join("")
  }

  return ""
}

const getReactNodeClassName = (node: any): string => {
  if (!isValidElement(node)) return ""

  const ownClassName = String((node.props as any)?.className || "")
  const childClassName = Children.toArray((node.props as any)?.children)
    .map(getReactNodeClassName)
    .filter(Boolean)
    .join(" ")

  return [ownClassName, childClassName].filter(Boolean).join(" ")
}

const getDataIndexValue = <T extends Record<string, any>>(
  record: T,
  dataIndex: ReportColumn["dataIndex"]
) => {
  if (!dataIndex) return undefined

  const path = Array.isArray(dataIndex)
    ? dataIndex
    : String(dataIndex).split(".")
  return path.reduce<any>((value, key) => value?.[key], record)
}

export default EnhancedReportTable
